import { format, isValid, parse, parseISO } from 'date-fns';
import type {
  ChartRuleColumn,
  CustomChartBarOrder,
  CustomChartDataResponse,
  CustomChartPoint,
  CustomChartValueType,
} from '@/entities/dashboards';
import { fmt } from '../utils';
import { groupChartItems } from './chartElements';

/**
 * Показ значений диаграммы: дату показывает клиент — он один знает часовой пояс смотрящего.
 * Бэкенд отдаёт даты машинной записью (`YYYY-MM-DD` и запись со смещением) и называет тип
 * значения, а привычный пользователю вид `дд.мм.гггг`, значимость времени в нём и порядок строк
 * по самой дате задаются здесь.
 */

/** Значение приходит машинной записью даты — с временем или без него. */
export const isChartDateType = (type: CustomChartValueType): boolean => type === 'date' || type === 'timestamp';

/**
 * Точность, с какой показаны значения-даты одного ряда: только дата, дата с временем до минут или
 * до секунд. Долей секунды не бывает ни при какой точности.
 */
export type ChartDatePrecision = 'date' | 'minute' | 'second';

/** Тип значения колонки выборки в обозначениях диаграммы: числом приходят и целые, и дробные. */
export const chartValueType = (selectionType: string | undefined): CustomChartValueType => {
  if (selectionType === 'date' || selectionType === 'timestamp') return selectionType;
  if (selectionType === 'int' || selectionType === 'numeric') return 'number';
  return 'text';
};

/** Тип значения диаграммы: берётся по имени колонки выборки, из которой значение собрано. */
export const chartColumnType = (columns: ChartRuleColumn[], name: string): CustomChartValueType =>
  chartValueType(columns.find((column) => column.name === name)?.type);

/** Момент времени машинной записи даты; не разобравшееся значение — `null`. */
const chartDateMoment = (raw: string, type: CustomChartValueType): Date | null => {
  // Дата без времени разбирается как местная: машинная запись `YYYY-MM-DD` часового пояса не
  // называет, и прочтение её как времени UTC сдвигало бы день у части пользователей.
  const moment = type === 'date' ? parse(raw, 'yyyy-MM-dd', new Date()) : parseISO(raw);
  return isValid(moment) ? moment : null;
};

/**
 * Точность показа ряда значений-дат: её задают сами данные.
 *
 * Время показывается, только если хотя бы одно значение ряда приходится не на начало суток: у
 * распределения по дням оно одинаково у всех значений и ничего не различает. Показанное время
 * обрезается до разряда, которым значения различаются: нулевые у всего ряда секунды не пишутся,
 * долей секунды нет никогда.
 *
 * Точность выбирается на весь ряд сразу — колонку таблицы или подписи одной диаграммы, — чтобы
 * соседние значения были написаны одинаково.
 */
export const chartDatePrecision = (
  values: (string | number | null | undefined)[],
  type: CustomChartValueType,
): ChartDatePrecision => {
  // Машинная запись даты без времени времени и не несёт — разрядам ряда взяться неоткуда.
  if (type !== 'timestamp') return 'date';

  let precision: ChartDatePrecision = 'date';
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const moment = chartDateMoment(String(value), type);
    if (!moment) continue;
    if (moment.getSeconds() !== 0 || moment.getMilliseconds() !== 0) return 'second';
    if (moment.getHours() !== 0 || moment.getMinutes() !== 0) precision = 'minute';
  }
  return precision;
};

/** Написание даты каждой точности: доли секунды не показываются ни при какой. */
const CHART_DATE_FORMAT: Record<ChartDatePrecision, string> = {
  date: 'dd.MM.yyyy',
  minute: 'dd.MM.yyyy HH:mm',
  second: 'dd.MM.yyyy HH:mm:ss',
};

/**
 * Дата в привычном пользователю виде и в часовом поясе браузера: `дд.мм.гггг`, а время рядом с
 * ней — с точностью, взятой по всему ряду ({@link chartDatePrecision}).
 *
 * Значение, не разобравшееся в дату, показывается как пришло: своей записи данным выборки клиент
 * не навязывает.
 */
export const formatChartDate = (raw: string, type: CustomChartValueType, precision: ChartDatePrecision): string => {
  const moment = chartDateMoment(raw, type);
  if (!moment) return raw;
  return format(moment, CHART_DATE_FORMAT[type === 'date' ? 'date' : precision]);
};

/**
 * Подпись элемента или заголовок колонки диаграммы в показанном виде: с заглавной первой буквы,
 * остальной текст — как пришёл.
 *
 * Заглавная ставится только при показе, а не в данных: по самой подписи диаграмма узнаёт
 * категорию — ей задают цвет поимённо, её значение уходит в связанную диаграмму, — и приведённая
 * в данных подпись перестала бы совпадать с тем, что дала выборка.
 */
export const shownChartLabel = (label: string): string => label.charAt(0).toLocaleUpperCase('ru') + label.slice(1);

/**
 * Величина, показанная рядом с названием элемента — у строки списка диалогов и у категории
 * показателя с переходом к выборке.
 *
 * Показывается тем видом, каким её дала выборка: число — с точностью своего ряда
 * (`chartNumberDigits`), дата — тем же написанием, что и подписи диаграмм, прочее — как
 * пришло. Тире стоит только там, где величины нет вовсе: на месте непустого значения оно читалось
 * бы как потерянные данные.
 */
export const shownChartValue = (
  value: string | number | null | undefined,
  type: CustomChartValueType,
  precision: ChartDatePrecision,
  digits: number,
): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (isChartDateType(type)) return formatChartDate(String(value), type, precision);
  // Тип колонки называет выборка, а пришедшее значение может быть и не числом: считаем числом
  // только то, что им и оказалось, — иначе на месте величины встало бы «NaN».
  const asNumber = Number(value);
  if (type === 'number' && Number.isFinite(asNumber)) return fmt(asNumber, digits);
  return String(value);
};

/**
 * Сравнение значений колонки для упорядочения строк: даты сравниваются моментами времени, числа —
 * величиной, прочее — как текст. Пустое значение уходит в конец при любом направлении.
 *
 * Даты сравниваются по машинному значению, а не по показанному написанию: в `дд.мм.гггг` день
 * стоит впереди года, и порядок по написанию перемешал бы годы.
 */
export const compareChartValues = (
  first: string | number | null,
  second: string | number | null,
  type: CustomChartValueType,
): number => {
  if (first === null || first === '') return second === null || second === '' ? 0 : 1;
  if (second === null || second === '') return -1;

  if (isChartDateType(type)) {
    const firstMoment = chartDateMoment(String(first), type);
    const secondMoment = chartDateMoment(String(second), type);
    if (firstMoment && secondMoment) return firstMoment.getTime() - secondMoment.getTime();
  }

  if (type === 'number') {
    const firstNumber = Number(first);
    const secondNumber = Number(second);
    if (Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) return firstNumber - secondNumber;
  }

  return String(first).localeCompare(String(second), 'ru');
};

/**
 * Алфавитное сравнение подписей: без учёта регистра, а числа внутри подписи — по величине, поэтому
 * «10» идёт после «9», а не перед ним.
 */
const LABEL_COLLATOR = new Intl.Collator('ru', { sensitivity: 'accent', numeric: true });

/** Сравнение подписей элементов диаграммы: даты — моментами, числа — величиной, текст — по алфавиту. */
const compareChartLabels = (first: string, second: string, type: CustomChartValueType): number =>
  type === 'text' ? LABEL_COLLATOR.compare(first, second) : compareChartValues(first, second, type);

/**
 * Ответ наполнения, где элементы столбчатой диаграммы стоят в порядке `order`; порядка нет —
 * ответ как есть.
 *
 * Порядок наводится по машинным значениям, до приведения подписей к показанному виду: дата
 * сравнивается моментом времени, а не написанием `дд.мм.гггг`, где день стоит впереди года.
 *
 * Элементы упорядочиваются внутри своих групп, а сами группы остаются в порядке первого появления.
 * Равные элементы сохраняют порядок выборки; пустая величина и пустая подпись уходят в конец при
 * любом направлении. Порядок — оформление: он перерисовывает уже полученные данные.
 */
export const withBarOrder = (
  data: CustomChartDataResponse,
  order: CustomChartBarOrder | undefined,
): CustomChartDataResponse => {
  if (data.chartType !== 'bar' || !order) return data;

  const labelType = chartColumnType(data.columns, 'label');
  const sign = order.direction === 'asc' ? 1 : -1;

  const compareInGroup = (first: CustomChartPoint, second: CustomChartPoint): number => {
    if (order.by === 'value') {
      if (first.value === null || second.value === null) {
        return Number(first.value === null) - Number(second.value === null);
      }
      return sign * (first.value - second.value);
    }
    if (!first.label || !second.label) return Number(!first.label) - Number(!second.label);
    return sign * compareChartLabels(first.label, second.label, labelType);
  };

  // Сортировка массива устойчива: равные элементы остаются в порядке выборки
  const points = groupChartItems(data.payload.points).flatMap((group) => [...group.items].sort(compareInGroup));
  return { ...data, payload: { points } };
};

/** Колонка выборки, значения которой таблица разворачивает в колонки. */
const PIVOT_COLUMN_NAME = 'pivot_column';

/**
 * Ответ наполнения, в котором подписи диаграммы показаны так, как их читает пользователь.
 *
 * Подписи приводятся к показанному виду разом во всём ответе: тепловая карта сопоставляет ячейки
 * со строками и столбцами по самим подписям, и приведи их каждое место отрисовки по отдельности —
 * ячейки перестали бы сходиться со своими заголовками. Значения таблиц остаются машинными: их вид
 * и порядок задаёт тип колонки при построении самой таблицы; подписью у таблицы служит заголовок
 * развёрнутой колонки — он и приводится.
 *
 * Ряд подписей — весь: точность времени берётся по нему целиком, поэтому подписи одной диаграммы
 * написаны с одной точностью, а соседняя диаграмма может быть подписана иначе.
 */
export const withShownLabels = (data: CustomChartDataResponse): CustomChartDataResponse => {
  switch (data.chartType) {
    case 'bar':
    case 'pie':
    case 'line': {
      const type = chartColumnType(data.columns, 'label');
      if (!isChartDateType(type)) return data;
      const precision = chartDatePrecision(
        data.payload.points.map((point) => point.label),
        type,
      );
      return {
        ...data,
        payload: {
          points: data.payload.points.map((point) => ({
            ...point,
            label: formatChartDate(point.label, type, precision),
          })),
        },
      };
    }
    case 'selection-breakdown': {
      const type = chartColumnType(data.columns, 'label');
      if (!isChartDateType(type)) return data;
      const precision = chartDatePrecision(
        data.payload.items.map((item) => item.label),
        type,
      );
      return {
        ...data,
        payload: {
          items: data.payload.items.map((item) => ({ ...item, label: formatChartDate(item.label, type, precision) })),
        },
      };
    }
    case 'conversation-list': {
      const type = chartColumnType(data.columns, 'label');
      if (!isChartDateType(type)) return data;
      const precision = chartDatePrecision(
        data.payload.items.map((item) => item.title),
        type,
      );
      return {
        ...data,
        payload: {
          items: data.payload.items.map((item) => ({ ...item, title: formatChartDate(item.title, type, precision) })),
        },
      };
    }
    case 'table': {
      // Заголовок развёрнутой колонки — значение выборки, и дата в нём написана машинной записью,
      // как подпись. Ряд — все развёрнутые колонки: месяцы подписаны одинаково.
      const type = chartColumnType(data.columns, PIVOT_COLUMN_NAME);
      if (!isChartDateType(type)) return data;
      const pivotTitles = data.payload.columns.filter((column) => column.pivot).map((column) => column.title);
      const precision = chartDatePrecision(pivotTitles, type);
      return {
        ...data,
        payload: {
          ...data.payload,
          columns: data.payload.columns.map((column) =>
            column.pivot ? { ...column, title: formatChartDate(column.title, type, precision) } : column,
          ),
        },
      };
    }
    case 'heatmap': {
      const rowType = chartColumnType(data.columns, 'row');
      const columnType = chartColumnType(data.columns, 'column');
      if (!isChartDateType(rowType) && !isChartDateType(columnType)) return data;
      // Заголовки строк и заголовки столбцов — два разных ряда: точность у каждого своя.
      const rowPrecision = chartDatePrecision(data.payload.rows, rowType);
      const columnPrecision = chartDatePrecision(data.payload.columns, columnType);
      const shownRow = (value: string) =>
        isChartDateType(rowType) ? formatChartDate(value, rowType, rowPrecision) : value;
      const shownColumn = (value: string) =>
        isChartDateType(columnType) ? formatChartDate(value, columnType, columnPrecision) : value;
      return {
        ...data,
        payload: {
          rows: data.payload.rows.map(shownRow),
          columns: data.payload.columns.map(shownColumn),
          cells: data.payload.cells.map((cell) => ({
            ...cell,
            row: shownRow(cell.row),
            column: shownColumn(cell.column),
          })),
        },
      };
    }
    default:
      return data;
  }
};
